import { z } from 'zod';
/**
 * "Doanh thu thực nhận": từ số YouTube ước tính đến số công ty thật sự nhận.
 *
 * Bốn khoản bị trừ, theo đúng thứ tự đó:
 * 1. **thuế Mỹ** — Google giữ lại trên phần doanh thu phát sinh ở Mỹ (mặc định
 *    30%, tuỳ hiệp định thuế của từng kênh);
 * 2. **phần Network** — tỷ lệ MCN giữ, 0 khi kênh nhận tiền trực tiếp qua AdSense;
 * 3. **VAT** — thuế công ty phải nộp trên phần nhận được (mặc định 10%).
 *
 * Mọi phép tính làm trên **cent (số nguyên)**, làm tròn nửa lên ở từng bước, nên
 * kết quả không phụ thuộc thứ tự dấu phẩy động. Lý do: tiền trong app lưu
 * `Decimal(14,4)` và báo cáo hiển thị hai số lẻ — cộng dồn `number` sẽ lệch cent
 * khi nhiều kênh nhiều kỳ.
 */
export const CACH_NHAN_TIEN = ['NETWORK', 'ADSENSE'];
export const cachNhanTienSchema = z.enum(CACH_NHAN_TIEN);
/** Phần trăm 0–100, bước 0,5. */
const phanTramSchema = z.number().min(0).max(100);
export const caiDatThucNhanSchema = z.object({
    /**
     * Suy ra từ loại đối tác của link phủ kỳ đó, **không ai đặt tay**: link trỏ
     * vào một tài khoản AdSense thì là `ADSENSE`, trỏ vào MCN thì là `NETWORK`.
     */
    cachNhanTien: cachNhanTienSchema,
    /**
     * Thuế Google giữ trên doanh thu phát sinh ở Mỹ. Lấy từ
     * `partner_contracts.thueMyPct` của hợp đồng hiệu lực theo **từng ngày**
     * trong kỳ (bình quân theo số ngày), không còn đặt riêng cho kênh.
     */
    thueMyPct: phanTramSchema,
    /** VAT trên phần công ty nhận được. */
    vatPct: phanTramSchema,
    /** Tỷ lệ Network giữ; bỏ qua khi `cachNhanTien = 'ADSENSE'`. */
    networkPct: phanTramSchema,
});
export const MAC_DINH_THUC_NHAN = { thueMyPct: 30, vatPct: 10 };
/** Hợp đồng cấp thuế Mỹ cho kỳ: để giao diện ghi "Thuế Mỹ x% theo <số HĐ>". */
export const hopDongThueMySchema = z.object({
    contractId: z.string(),
    contractNo: z.string(),
    partnerId: z.string(),
    partnerName: z.string(),
    laAdsense: z.boolean(),
    thueMyPct: z.number(),
});
export const buocThucNhanSchema = z.object({
    khoa: z.string(),
    nhan: z.string(),
    giaTri: z.number(),
    /** `tru` = dòng bị trừ, `tong` = dòng cộng dồn, `cuoi` = dòng kết quả. */
    kieu: z.enum(['tong', 'tru', 'cuoi']),
    ghiChu: z.string().nullable(),
});
export const thucNhanSchema = z.object({
    doanhThu: z.number(),
    /** Phần doanh thu phát sinh ở Mỹ (`dimensions=country`, dòng `US`). */
    doanhThuMy: z.number(),
    thueMy: z.number(),
    sauThue: z.number(),
    phanNetwork: z.number(),
    congTyNhan: z.number(),
    vat: z.number(),
    thucNhan: z.number(),
    caiDat: caiDatThucNhanSchema,
    /** Tên Network để hiển thị; `null` khi nhận qua AdSense. */
    tenNetwork: z.string().nullable(),
    buoc: z.array(buocThucNhanSchema),
});
/** Đổi tiền sang cent (số nguyên), làm tròn nửa lên. */
function sangCent(tien) {
    return Math.round(tien * 100);
}
/** Cent → tiền hai số lẻ. */
function sangTien(cent) {
    return Math.round(cent) / 100;
}
/**
 * `cent × pct%`, làm tròn nửa lên về cent.
 *
 * Phần trăm quy về "điểm cơ bản" (30% → 3000) để nhân chia toàn số nguyên.
 */
function phanTramCua(cent, pct) {
    const diem = Math.round(pct * 100);
    return Math.round((cent * diem) / 10_000);
}
/**
 * Tính thực nhận theo cài đặt của kênh.
 *
 * Ví dụ kênh Pain (11/09–08/10/2026): doanh thu 1.192,02, phần Mỹ 514,41, thuế
 * Mỹ 30%, Network 50%, VAT 10% → thực nhận **466,96 US$**. Cùng số đó mà nhận
 * trực tiếp qua AdSense → **933,93 US$**.
 */
export function tinhThucNhan(dau) {
    const { caiDat } = dau;
    const quaNetwork = caiDat.cachNhanTien === 'NETWORK';
    const doanhThuCent = sangCent(dau.doanhThu);
    const myCent = sangCent(dau.doanhThuMy);
    const thueMyCent = phanTramCua(myCent, caiDat.thueMyPct);
    const sauThueCent = doanhThuCent - thueMyCent;
    const networkCent = quaNetwork ? phanTramCua(sauThueCent, caiDat.networkPct) : 0;
    const congTyCent = sauThueCent - networkCent;
    const vatCent = phanTramCua(congTyCent, caiDat.vatPct);
    const thucNhanCent = congTyCent - vatCent;
    const soPct = (x) => `${x.toLocaleString('vi-VN', { maximumFractionDigits: 1 })}%`;
    const buoc = [
        {
            khoa: 'doanhThu',
            nhan: 'Doanh thu ước tính (YouTube)',
            giaTri: sangTien(doanhThuCent),
            kieu: 'tong',
            ghiChu: null,
        },
        {
            khoa: 'thueMy',
            nhan: `Thuế Mỹ ${soPct(caiDat.thueMyPct)}`,
            giaTri: -sangTien(thueMyCent),
            kieu: 'tru',
            ghiChu: `trên phần doanh thu ở Mỹ: ${sangTien(myCent).toLocaleString('vi-VN', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })} US$`,
        },
        {
            khoa: 'sauThue',
            nhan: 'Còn lại sau thuế Mỹ',
            giaTri: sangTien(sauThueCent),
            kieu: 'tong',
            ghiChu: null,
        },
        {
            khoa: 'phanNetwork',
            nhan: quaNetwork
                ? `Phần Network ${dau.tyLeBinhQuan === true ? 'bình quân ' : ''}${soPct(caiDat.networkPct)}${dau.tenNetwork ? ` · ${dau.tenNetwork}` : ''}`
                : 'Phần Network',
            giaTri: -sangTien(networkCent),
            kieu: 'tru',
            ghiChu: quaNetwork ? null : 'nhận tiền trực tiếp qua AdSense, không qua Network',
        },
        {
            khoa: 'congTyNhan',
            nhan: 'Công ty nhận',
            giaTri: sangTien(congTyCent),
            kieu: 'tong',
            ghiChu: null,
        },
        {
            khoa: 'vat',
            nhan: `VAT ${soPct(caiDat.vatPct)}`,
            giaTri: -sangTien(vatCent),
            kieu: 'tru',
            ghiChu: null,
        },
        {
            khoa: 'thucNhan',
            nhan: 'Thực nhận sau thuế',
            giaTri: sangTien(thucNhanCent),
            kieu: 'cuoi',
            ghiChu: null,
        },
    ];
    return {
        doanhThu: sangTien(doanhThuCent),
        doanhThuMy: sangTien(myCent),
        thueMy: sangTien(thueMyCent),
        sauThue: sangTien(sauThueCent),
        phanNetwork: sangTien(networkCent),
        congTyNhan: sangTien(congTyCent),
        vat: sangTien(vatCent),
        thucNhan: sangTien(thucNhanCent),
        caiDat,
        tenNetwork: quaNetwork ? (dau.tenNetwork ?? null) : null,
        buoc,
    };
}
/* ------------------------------------------------------------------ */
/* DTO của màn hình cài đặt                                            */
/* ------------------------------------------------------------------ */
export const dongLichSuThucNhanSchema = z.object({
    hieuLucTu: z.string(),
    vatPct: z.number(),
    networkPct: z.number(),
    nguoiSua: z.string().nullable(),
    taoLuc: z.iso.datetime(),
});
export const caiDatThucNhanKenhSchema = z.object({
    channelId: z.string(),
    caiDat: caiDatThucNhanSchema,
    /** `false` = chưa ai đặt, đang dùng mặc định + tỷ lệ Network của hợp đồng. */
    daDat: z.boolean(),
    /** Ngày bắt đầu hiệu lực của bản đang dùng; `null` khi chưa đặt. */
    hieuLucTu: z.string().nullable(),
    tenNetwork: z.string().nullable(),
    /**
     * `true` khi kỳ có ngày **không thuộc Network nào** — thường là do ngày vào
     * Network nhập sai. Giao diện phải cảnh báo thay vì âm thầm tính 0%.
     */
    thieuNetwork: z.boolean(),
    soNgayThieuNetwork: z.number().int().nonnegative(),
    /**
     * `true` khi `networkPct` là bình quân của nhiều tỷ lệ trong kỳ, không phải
     * tỷ lệ của một hợp đồng. Xem `DauVaoThucNhan.tyLeBinhQuan`.
     */
    tyLeBinhQuan: z.boolean(),
    /**
     * Hợp đồng cấp thuế Mỹ cho kỳ. Nhiều hợp đồng cắt ngang kỳ thì lấy hợp đồng
     * của **ngày cuối kỳ**; `null` khi không ngày nào có hợp đồng.
     */
    hopDongThueMy: hopDongThueMySchema.nullable(),
    /**
     * `true` khi kỳ có ngày **chưa khai thuế Mỹ** (không có hợp đồng nào phủ
     * ngày đó). Những ngày đó tính theo mặc định 30% và giao diện phải cảnh báo.
     */
    thieuThueMy: z.boolean(),
    soNgayThieuThueMy: z.number().int().nonnegative(),
    lichSu: z.array(dongLichSuThucNhanSchema),
});
/**
 * Lưu cài đặt thực nhận của kênh: **chỉ còn VAT và tỷ lệ Network ghi đè**.
 *
 * `thueMyPct` và `cachNhanTien` bỏ khỏi đây (migration
 * `20261011125839_adsense_truc_tiep_va_thue_my_theo_hop_dong`): thuế Mỹ thuộc
 * hợp đồng của đối tác, cách nhận tiền suy ra từ loại đối tác. Để hai nguồn
 * cùng khai một số là mở đường cho báo cáo lệch nhau.
 */
export const luuCaiDatThucNhanSchema = z.object({
    vatPct: phanTramSchema,
    networkPct: phanTramSchema,
    /** Mặc định là hôm nay (giờ Việt Nam) nếu không truyền. */
    hieuLucTu: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
//# sourceMappingURL=thucNhan.js.map